import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './compute-jobs.css';

// classes
import JobsList from './jobs-list';
import JobSubmissionForm from './job-submission-form';
import ToolButton, { ToolButtonType } from '../../tools/tool-button';
import Scrollbox from '../../tools/scrollbox';
import MiniButton from '../../tools/mini-button';
import SiteIcon from '../../tools/site-icon';
import StatusIndicator from '../../tools/status-indicator';

// types
import { Event, EventTarget, EventType } from '../../utils/events';
import { ComputeJob } from '../compute-jobs/types';

// functions
import { addLineBreaks } from '../../utils/functions';

// icons
import {
  CircleAlert,
  CircleCheck,
  CirclePlay,
  Clock,
  Cog,
  Copy,
  Flag,
  HardDrive,
  Pencil,
  RefreshCw,
  ScrollText,
} from 'lucide-react';

// --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { handleEvent } from '../../app-actions';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

import { setDisplayedJob } from './compute-jobs-slice';

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

const ComputeJobs: React.FC = () => {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // local state variables.
  const sDisplayedJob: ComputeJob | undefined = useAppSelector(
    (state) => state.computeJobs.displayedJob,
  );

  // global state variables.
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // data state variables.
  const sD_computeJobs: ComputeJob[] = useAppSelector((state) => state.data.computeJobs);
  const sD_computeJobsLoading: boolean = useAppSelector(
    (state) => state.data.computeJobsLoading,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  const { t } = useTranslation();

  const [showSubmissionForm, setShowSubmissionForm] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  const [sListKey, setListKey] = useState<number>(0);
  const refEventToJobsList = useRef<any>();

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  format a timestamp to the user's locale.
  //
  //  ------------------------------------------------------------

  const formatTimestamp = (timestamp: string): string => {
    //
    const date = new Date(timestamp);
    return date.toLocaleString();
    //
  }; // formatTimestamp

  const handleSubmitJob = () => {
    setShowSubmissionForm(true);
  };

  const handleFormClose = () => {
    setShowSubmissionForm(false);
  };

  const handleJobSubmitted = (jobId: string) => {
    setShowSubmissionForm(false);
    setSuccessMessage(`Job ${jobId} submitted successfully!`);

    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage(undefined);
    }, 5000);

    // Trigger refresh of jobs list
    if (refEventToJobsList.current) {
      refEventToJobsList.current({
        event: {
          eventTarget: EventTarget.COMPUTE_JOBS,
          eventType: EventType.COMPUTE_JOB_ADDED,
        },
      });
    }
  };

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <div
      className="panel-area fr"
      style={{
        flex: '1 1',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <div
        className={'panel-area fc' + (sDisplayedJob !== undefined ? ' br-d-r' : '')}
        style={{
          flex: '1 1',
          maxHeight: '100%',
          height: '100%',
        }}
      >
        <div className="panel-header" style={{ flex: '0 0 auto', marginBottom: '8px' }}>
          <div
            id="lbl-compute-jobs"
            role="heading"
            aria-level={2}
            className="header-text"
            style={{ flex: '1 1 0px' }}
          >
            {t('Status of compute jobs')}
          </div>
        </div>

        {sD_computeJobs.length === 0 && sG_loggedIn === true && (
          <div className="no-data-overlay">No compute jobs</div>
        )}
        {sG_loggedIn !== true && <div className="no-data-overlay">Login required</div>}

        <Scrollbox
          direction="vertical"
          flex="1 1"
          width="100%"
          flexDirection="column"
          margin="5px"
        >
          <JobsList
            key={sListKey}
            setEventHandler={(handler: any) => (refEventToJobsList.current = handler)}
          />
        </Scrollbox>

        <div style={{ flex: '0 0 5px' }} />
        <div
          style={{
            width: '100%',
            height: 'auto',
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
          }}
        >
          <div style={{ flex: '1 1' }} />
          <MiniButton
            id="refresh-button"
            className="text-colour"
            style={{ flex: '0 0 auto', height: '34px' }}
            onClick={() => {
              const computeJobEvent: Event = {
                eventTarget: EventTarget.APP,
                eventType: EventType.GET_COMPUTE_JOBS,
              };
              dispatch(handleEvent({ event: computeJobEvent }));
            }}
            title={t('Refresh')}
            aria-label={t('Refresh')}
            data-mini-button="T"
          >
            <RefreshCw width={30} height={30} strokeWidth={1.5} />
          </MiniButton>
          <ToolButton
            name="submit-job"
            text={t('Submit job')}
            onClick={handleSubmitJob}
            tooltip="Submit job"
            type={ToolButtonType.SECONDARY}
          />
          <div style={{ flex: '0 0 5px' }} />
        </div>

        {successMessage && (
          <div className="success-message">
            <span>{successMessage}</span>
          </div>
        )}
      </div>
      {sDisplayedJob !== undefined && (
        <div
          className="panel-area fc text-colour"
          style={{
            flex: '1 1',
            maxHeight: '100%',
            height: '100%',
            marginLeft: '10px',
            padding: '10px',
          }}
        >
          <div
            role="heading"
            aria-level={2}
            className="header-text"
            style={{
              paddingBottom: '35px',
            }}
          >
            Details on compute job {sDisplayedJob.job_id}
          </div>
          <div
            className="fr"
            style={{
              alignItems: 'center',
              marginBottom: '20px',
              width: '100%',
            }}
          >
            <div style={{ flex: '0 0 70px', marginLeft: '10px' }}>
              <Cog width={30} height={30} strokeWidth={1.5} />
            </div>
            <div style={{ flex: '1 1' }}>{sDisplayedJob.job_name}</div>
          </div>
          <Scrollbox
            direction="vertical"
            flex="1 1"
            width="100%"
            flexDirection="column"
            margin="5px 5px 15px 10px"
          >
            <div
              className="fr"
              style={{
                alignItems: 'center',
                marginBottom: '10px',
                width: '100%',
              }}
            >
              <div style={{ flex: '0 0 70px' }}>
                <Flag width={30} height={30} strokeWidth={1.5} />
              </div>
              <div style={{ flex: '0 0 180px' }}>Status:</div>
              <div
                style={{
                  flex: '1 1',
                }}
              >
                <StatusIndicator status={sDisplayedJob.status} />
              </div>
            </div>
            <div
              className="fr"
              style={{
                alignItems: 'center',
                marginTop: '15px',
                marginBottom: '10px',
                width: '100%',
              }}
            >
              <div style={{ flex: '0 0 70px' }}>
                <Clock width={30} height={30} strokeWidth={1.5} />
              </div>
              <div style={{ flex: '0 0 180px' }}>Submitted:</div>
              <div
                style={{
                  flex: '1 1',
                }}
              >
                {formatTimestamp(sDisplayedJob.submitted_at)}
              </div>
            </div>
            {sDisplayedJob.started_at !== undefined && (
              <div
                className="fr"
                style={{
                  alignItems: 'center',
                  marginTop: '15px',
                  marginBottom: '10px',
                  width: '100%',
                }}
              >
                <div style={{ flex: '0 0 70px' }}>
                  <CirclePlay width={30} height={30} strokeWidth={1.5} />
                </div>
                <div style={{ flex: '0 0 180px' }}>Started at:</div>
                <div
                  style={{
                    flex: '1 1',
                  }}
                >
                  {formatTimestamp(sDisplayedJob.started_at)}
                </div>
              </div>
            )}
            {sDisplayedJob.completed_at !== undefined && (
              <div
                className="fr"
                style={{
                  alignItems: 'center',
                  marginTop: '15px',
                  marginBottom: '10px',
                  width: '100%',
                }}
              >
                <div style={{ flex: '0 0 70px' }}>
                  <CircleCheck width={30} height={30} strokeWidth={1.5} />
                </div>
                <div style={{ flex: '0 0 180px' }}>Completed at:</div>
                <div
                  style={{
                    flex: '1 1',
                  }}
                >
                  {formatTimestamp(sDisplayedJob.completed_at)}
                </div>
              </div>
            )}
            {sDisplayedJob.updated_at !== undefined && (
              <div
                className="fr"
                style={{
                  alignItems: 'center',
                  marginTop: '15px',
                  marginBottom: '10px',
                  width: '100%',
                }}
              >
                <div style={{ flex: '0 0 70px' }}>
                  <Pencil width={30} height={30} strokeWidth={1.5} />
                </div>
                <div style={{ flex: '0 0 180px' }}>Updated at:</div>
                <div
                  style={{
                    flex: '1 1',
                  }}
                >
                  {formatTimestamp(sDisplayedJob.updated_at)}
                </div>
              </div>
            )}
            {(sDisplayedJob.error?.message ?? '') !== '' && (
              <>
                <div
                  className="fr"
                  style={{
                    alignItems: 'center',
                    marginTop: '15px',
                    marginBottom: '20px',
                    width: '100%',
                  }}
                >
                  <div style={{ flex: '0 0 70px' }}>
                    <CircleAlert width={30} height={30} strokeWidth={1.5} />
                  </div>
                  Error Details:
                </div>
                <div
                  className="fr"
                  style={{
                    width: '100%',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    className="border-colour"
                    style={{
                      flex: '1 1',
                      overflowWrap: 'anywhere',
                      borderRadius: '10px',
                      paddingLeft: '20px',
                      padding: '10px',
                      margin: '0px 10px 0px 70px',
                    }}
                    data-faint-border="T"
                  >
                    {addLineBreaks(sDisplayedJob.error?.message)}
                  </div>
                  <div
                    className="fc"
                    style={{
                      flex: '0 0 40px',
                      marginRight: '10px',
                    }}
                  >
                    <div>
                      <MiniButton
                        className="text-colour"
                        data-mini-button="T"
                        title="Copy error message"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            sDisplayedJob.error?.message ?? '',
                          )
                        }
                      >
                        <Copy width={30} height={30} strokeWidth={1.5} />
                      </MiniButton>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Scrollbox>
          <div
            className="fr"
            style={{
              width: '100%',
              justifyContent: 'right',
            }}
          >
            <ToolButton
              key={t('Close')}
              name="close-dialog"
              onClick={() => dispatch(setDisplayedJob(undefined))}
              tooltip="Close this information panel"
              text={t('Close')}
              type={ToolButtonType.SECONDARY}
            />
          </div>
        </div>
      )}

      <JobSubmissionForm
        show={showSubmissionForm}
        onClose={handleFormClose}
        onSuccess={handleJobSubmitted}
      />
    </div>
  );
  //
}; // ComputeJobs

export default ComputeJobs;
